import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';

/*
 * Usage & charging: every company's AI, compute and AWS hosting at what it
 * cost us and what we charged, and the switch that decides whether we charge it.
 * Rendered inside the Command Deck, so it uses the deck's own cd- classes.
 */

type Period = 'month' | '30d' | 'all';

interface CompanyRow {
  id: string;
  name: string | null;
  owner_email: string | null;
  plan: 'free' | 'team' | 'legacy';
  charged: boolean;
  not_billed_source: 'super_admin' | null;
  note: string | null;
  set_by: string | null;
  set_at: string | null;
  ai_cost_usd: number;
  ai_charge_usd: number;
  compute_cost_usd: number;
  compute_charge_usd: number;
  aws_cost_usd: number;
  aws_charge_usd: number;
  cost_usd: number;
  would_charge_usd: number;
  charged_usd: number;
  margin_usd: number;
  calls: number;
  uncharged_calls: number;
  box_seconds: number;
}

interface CallRow {
  at: string;
  who: string | null;
  model: string | null;
  feature: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheTokens: number;
  costUsd: number;
  chargeUsd: number;
  charged: boolean;
  reason: string | null;
  basis: string;
  marginPercent: number | null;
}

interface BoxRow {
  startedAt: string;
  seconds: number;
  vcpus: number | null;
  memoryMb: number | null;
  costUsd: number;
  chargeUsd: number;
}

interface HostingTotals { bills: number; bill_usd: number; charged_usd: number; absorbed_usd: number }

interface HistoryRow { charged: boolean; note: string | null; actor: string | null; at: string }

const API = () => `${getApiBaseUrl()}/super/billing/companies`;

const PERIODS: [Period, string][] = [['month', 'This month'], ['30d', 'Last 30 days'], ['all', 'All time']];

const usd = (n: number) => {
  const abs = Math.abs(n);
  const digits = abs > 0 && abs < 1 ? 4 : 2;
  const text = `$${abs.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  if (n < 0) return `-${text}`;
  return text;
};

/* Margin as a share of cost, the same way the margin setting works: +10%
 * means we charged cost plus 10%. */
const pct = (margin: number, cost: number) => {
  if (!cost && margin > 0) return 'flat fee';
  if (!cost) return '';
  const value = Math.round((margin / cost) * 100);
  return `${value > 0 ? '+' : ''}${value}%`;
};

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const PLAN_LABEL: Record<CompanyRow['plan'], string> = { free: 'Free', team: 'Team plan', legacy: 'Old plan' };

const errorText = (err: unknown, fallback: string) => {
  const detail = (err as { response?: { data?: { detail?: string | { message?: string } } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
};

const BASIS: Record<string, { label: string; tone: string }> = {
  margin: { label: 'Margin', tone: 'g' },
  'fixed-price': { label: 'Own price', tone: 'b' },
  'flat-fee-fixed-price': { label: 'Flat fee · own price', tone: 'b' },
  'flat-fee-unpriced': { label: 'Flat fee · no price', tone: 'w' },
  'no-price': { label: 'No price', tone: 'r' },
  'customer-funded': { label: 'Own AI account', tone: 'o' },
  'not-billed': { label: 'Not billed', tone: 'o' },
  'before-sell-pricing': { label: 'At cost (older)', tone: 'o' },
  unknown: { label: 'Older call', tone: 'o' },
};

const BasisChip: React.FC<{ basis: string; margin?: number | null }> = ({ basis, margin }) => {
  const b = BASIS[basis] || { label: '—', tone: 'o' };
  const label = basis === 'margin' && margin != null ? `Margin +${margin}%` : b.label;
  return <span className={`cd-chip ${b.tone}`}>{label}</span>;
};

/* What the company was charged for AWS hosting; its seat share underneath
 * when the two differ (absorbed, or moved by the super admin). */
const AwsShare: React.FC<{ row: CompanyRow }> = ({ row }) => {
  if (!row.aws_cost_usd && !row.aws_charge_usd) return <span className="cd-sub2">—</span>;
  if (!row.aws_charge_usd) return <>{usd(0)}<div className="cd-sub2">{usd(row.aws_cost_usd)} absorbed</div></>;
  if (Math.abs(row.aws_charge_usd - row.aws_cost_usd) < 0.005) return <>{usd(row.aws_charge_usd)}</>;
  return <>{usd(row.aws_charge_usd)}<div className="cd-sub2">seat share {usd(row.aws_cost_usd)}</div></>;
};

const ChargingChip: React.FC<{ row: CompanyRow }> = ({ row }) => {
  if (row.charged) return <span className="cd-chip g">Charged</span>;
  return <span className="cd-chip w">Not charged</span>;
};

const PAGE_SIZE = 50;

const Pager: React.FC<{ page: number; total: number; busy: boolean; onPage: (page: number) => void }> = ({ page, total, busy, onPage }) => {
  const pages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const first = (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="cu-pager">
      <span className="cd-sub2">{total === 0 ? 'No calls' : `Calls ${first.toLocaleString()} to ${last.toLocaleString()} of ${total.toLocaleString()}`}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {busy && <Loader2 size={12} className="animate-spin" />}
        <button type="button" className="cd-btn sm" onClick={() => onPage(page - 1)} disabled={busy || page <= 1} aria-label="Previous page"><ChevronLeft size={14} /> Previous</button>
        <span className="cd-sub2" style={{ fontSize: 12 }}>Page {page} of {pages}</span>
        <button type="button" className="cd-btn sm" onClick={() => onPage(page + 1)} disabled={busy || page >= pages} aria-label="Next page">Next <ChevronRight size={14} /></button>
      </span>
    </div>
  );
};

const CompanyPage: React.FC<{ row: CompanyRow; onBack: () => void; onChanged: () => void }> = ({ row, onBack, onChanged }) => {
  const [calls, setCalls] = useState<CallRow[] | null>(null);
  const [boxes, setBoxes] = useState<BoxRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [available, setAvailable] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [paging, setPaging] = useState(false);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (nextPage: number) => {
    const { data } = await axios.get(`${API()}/${row.id}/calls`, { params: { limit: PAGE_SIZE, page: nextPage } });
    setAvailable(data.usage_available);
    setCalls(data.calls);
    setTotal(typeof data.total === 'number' ? data.total : data.calls.length);
    setPage(nextPage);
    setBoxes(data.boxes);
    setHistory(data.history);
  }, [row.id]);

  useEffect(() => {
    load(1).catch(() => setAvailable(false));
  }, [load]);

  const goTo = async (nextPage: number) => {
    setPaging(true);
    try {
      await load(nextPage);
    } catch {
      setAvailable(false);
    } finally {
      setPaging(false);
    }
  };

  const switchCharging = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.put(`${API()}/${row.id}/charging`, { charged: !row.charged, note: note.trim() || null, cancel_plan: paysForPlan });
      setConfirming(false);
      setNote('');
      onChanged();
      load(page).catch(() => setAvailable(false));
    } catch (err) {
      setError(errorText(err, 'Could not change charging. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  // A company paying for a plan has its Stripe subscription cancelled first;
  // the server marks it not charged only once Stripe confirms.
  const paysForPlan = row.charged && (row.plan === 'team' || row.plan === 'legacy');
  let chargingLine = 'Seats and usage are billed normally.';
  let chargedHead = 'Charged';
  if (!row.charged) chargedHead = 'Would charge';
  if (!row.charged && row.not_billed_source === 'super_admin') {
    chargingLine = `Free: no seat fee, no usage taken, never paused.${row.note ? ` Reason: ${row.note}.` : ''}`;
  }

  return (
    <div className="cu-page">
        <button type="button" className="cd-link" onClick={onBack} style={{ alignSelf: 'flex-start' }}><ArrowLeft size={13} /> Usage &amp; charging</button>
        <div className="cu-head">
          <div style={{ minWidth: 0 }}>
            <div className="cu-title">{row.name || row.owner_email}</div>
            <div className="cd-sub2">{row.owner_email} · {PLAN_LABEL[row.plan]}</div>
          </div>
        </div>

        <div className="cu-body">
          <div className="cu-figs">
            <div><span className="cd-eyebrow">AI cost</span><b>{usd(row.ai_cost_usd)}</b></div>
            <div><span className="cd-eyebrow">E2B cost</span><b>{usd(row.compute_cost_usd)}</b></div>
            <div><span className="cd-eyebrow">AWS share</span><b>{usd(row.aws_cost_usd)} <small>{row.aws_charge_usd ? `charged ${usd(row.aws_charge_usd)}` : row.aws_cost_usd ? 'absorbed' : ''}</small></b></div>
            <div><span className="cd-eyebrow">Charged</span><b>{usd(row.charged_usd)} <small>{pct(row.margin_usd, row.cost_usd) ? `margin ${pct(row.margin_usd, row.cost_usd)}` : ''}</small></b></div>
            <div><span className="cd-eyebrow">Profit</span><b style={{ color: row.margin_usd < 0 ? 'var(--cd-red)' : 'var(--cd-good)' }}>{usd(row.margin_usd)}</b></div>
          </div>

          <div className="cu-charging">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
              <ChargingChip row={row} />
              <span className="cd-sub2" style={{ fontSize: 12 }}>{chargingLine}</span>
            </div>
            {!confirming && (
              <button type="button" className={`cd-btn sm${row.charged ? '' : ' pri'}`} onClick={() => setConfirming(true)}>
                {row.charged ? 'Stop charging' : 'Start charging'}
              </button>
            )}
          </div>
          {confirming && (
            <div className="cu-confirm">
              {row.charged && !paysForPlan && <b>Make this company free? Seats and usage stop being charged; usage is still tracked.</b>}
              {paysForPlan && (
                <div style={{ display: 'grid', gap: 4 }}>
                  <b>Stop charging {row.name || row.owner_email}?</b>
                  <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 3 }}>
                    <li>Its {PLAN_LABEL[row.plan]} is <b>cancelled in Stripe now</b>: no more monthly seat charges.</li>
                    <li>No refund or credit for the rest of this month.</li>
                    <li>Seats, AI and compute stop being charged. Usage is still tracked here.</li>
                    <li>Past invoices, charges and usage stay as they are.</li>
                  </ul>
                </div>
              )}
              {!row.charged && <b>Charge this company again? Usage is charged from now, and the company is on Free: its Owner buys the Team plan again for seats.</b>}
              <input id="charging-note" className="cd-input" value={note} maxLength={500} aria-label="Reason"
                placeholder="Reason (optional)" onChange={(e) => setNote(e.target.value)} />
              {error && <div role="alert" style={{ color: 'var(--cd-red)', fontSize: 12 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="cd-btn sm pri" onClick={switchCharging} disabled={busy}>
                  {busy && <Loader2 size={12} className="animate-spin" />} {paysForPlan ? 'Cancel plan and stop charging' : row.charged ? 'Stop charging' : 'Start charging'}
                </button>
                <button type="button" className="cd-btn sm" onClick={() => { setConfirming(false); setError(''); }} disabled={busy}>Cancel</button>
              </div>
            </div>
          )}

          <div className="cu-section">All AI calls</div>
          {!available && <div className="cd-sub2">The builder did not answer. Calls appear once it does.</div>}
          {available && calls === null && <div style={{ padding: 20, display: 'grid', placeItems: 'center' }}><Loader2 size={16} className="animate-spin" /></div>}
          {available && calls && calls.length === 0 && <div className="cd-sub2">No AI calls yet.</div>}
          {available && calls && calls.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table className="cd-t">
                <thead><tr><th>When</th><th>Model</th><th>Pricing</th><th className="r">Tokens</th><th className="r">Cost</th><th className="r">{chargedHead}</th></tr></thead>
                <tbody>
                  {calls.map((c, i) => (
                    <tr key={`${c.at}-${i}`}>
                      <td className="m" style={{ whiteSpace: 'nowrap' }}>{when(c.at)}<div className="cd-sub2">{c.who || ''}</div></td>
                      <td>{c.model || '—'}</td>
                      <td><BasisChip basis={c.basis} margin={c.marginPercent} /></td>
                      <td className="r m" title={`${c.inputTokens.toLocaleString()} in, ${c.outputTokens.toLocaleString()} out, ${c.cacheTokens.toLocaleString()} cache`}>
                        {(c.inputTokens + c.outputTokens + c.cacheTokens).toLocaleString()}
                      </td>
                      <td className="r m">{usd(c.costUsd)}</td>
                      <td className="r m">{c.charged ? usd(c.chargeUsd) : <span className="cd-sub2" title={c.reason || ''}>Not charged</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {available && calls && total > 0 && <Pager page={page} total={total} busy={paging} onPage={goTo} />}

          {boxes.length > 0 && (
            <>
              <div className="cu-section">All workspace boxes</div>
              <table className="cd-t">
                <thead><tr><th>Started</th><th className="r">Active</th><th className="r">Cost</th><th className="r">{chargedHead}</th></tr></thead>
                <tbody>
                  {boxes.map((b, i) => (
                    <tr key={`${b.startedAt}-${i}`}>
                      <td className="m">{when(b.startedAt)}</td>
                      <td className="r m">{Math.round(b.seconds / 60)} min</td>
                      <td className="r m">{usd(b.costUsd)}</td>
                      <td className="r m">{usd(b.chargeUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {history.length > 0 && (
            <>
              <div className="cu-section">Charging history</div>
              <ul className="cu-history">
                {history.map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    <b>{h.charged ? 'Charged' : 'Not charged'}</b> · {when(h.at)} · {h.actor}{h.note ? ` · ${h.note}` : ''}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
    </div>
  );
};

export const MODAL_CSS = `
.cu-page{background:var(--cd-panel);border:1px solid var(--cd-line);border-radius:12px;display:flex;flex-direction:column;padding:16px 20px 22px;gap:10px}
.cu-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;padding-bottom:14px;border-bottom:1px solid var(--cd-line)}
.cu-title{font-size:19px;font-weight:700;color:var(--cd-ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cu-body{padding-top:6px;display:flex;flex-direction:column;gap:14px}
.cu-pager{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;padding-top:4px}
.cu-figs{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
.cd-kpis.cu-k5{grid-template-columns:repeat(5,1fr)}
@media (max-width:1100px){.cd-kpis.cu-k5{grid-template-columns:repeat(3,1fr)}}
@media (max-width:640px){.cd-kpis.cu-k5{grid-template-columns:repeat(2,1fr)}}
.cu-figs>div{background:var(--cd-bg);border-radius:10px;padding:12px 14px;display:flex;flex-direction:column;gap:4px}
.cu-figs b{font-size:19px;font-variant-numeric:tabular-nums;color:var(--cd-ink)}
.cu-figs small{font-size:12px;font-weight:500;color:var(--cd-mut2)}
.cu-charging{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
.cu-confirm{display:grid;gap:8px;padding:12px;border:1px solid var(--cd-line);border-radius:10px;font-size:12.5px}
.cu-section{font-size:11px;letter-spacing:.09em;text-transform:uppercase;color:var(--cd-mut2);margin-top:4px}
.cu-history{margin:0;padding:0;list-style:none;display:grid;gap:6px;font-size:12px;color:var(--cd-muted)}
@media (max-width:640px){.cu-figs{grid-template-columns:1fr 1fr}}
`;

interface AuditModel {
  value: string; label: string; provider: string; billing: string; basis: string;
  costInput: number | null; costOutput: number | null; sellInput: number | null; sellOutput: number | null;
  markupPercent: number | null; warnings: string[];
}
interface PricingAudit {
  available?: boolean;
  marginPercent: number;
  computeMarginPercent: number;
  providers: { key: string; label: string; billingMode: string; monthlyPriceUsd: number | null; warnings: string[] }[];
  models: AuditModel[];
  unpricedInUsage: { model: string; calls: number; lastAt: string }[];
}

const rate = (n: number | null) => (n == null ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}`);

/* Every enabled model's pricing rule, so a fixed price or a missing price is
 * never the hidden reason behind a margin. */
const ModelPricingPanel: React.FC = () => {
  const [audit, setAudit] = useState<PricingAudit | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axios.get(`${getApiBaseUrl()}/super/billing/pricing-audit`)
      .then(({ data }) => { if (data.available === false) setFailed(true); else setAudit(data); })
      .catch(() => setFailed(true));
  }, []);

  if (failed) return <div className="cd-note"><b>Model pricing unavailable.</b> The builder did not answer.</div>;
  if (!audit) return null;

  return (
    <div className="cd-panel" style={{ marginTop: 16 }}>
      <div className="cd-ph">
        <span className="t">Model pricing</span>
        <span className="cd-sub2">Default rule: cost + {audit.marginPercent}% AI margin · E2B cost + {audit.computeMarginPercent}%</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 15px 12px' }}>
        {audit.providers.map((p) => (
          <span key={p.key} className={`cd-chip ${p.warnings.length ? 'w' : 'o'}`} title={p.warnings.join(' ')}>
            {p.label}: {p.billingMode === 'subscription' ? `flat fee${p.monthlyPriceUsd != null ? ` $${p.monthlyPriceUsd}/mo` : ''}` : 'per token'}
            {p.warnings.length > 0 && ' · fee not recorded'}
          </span>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="cd-t">
          <thead><tr>
            <th>Model</th><th>Rule</th><th className="r">Cost / 1M in · out</th><th className="r">We charge / 1M in · out</th><th>Check</th>
          </tr></thead>
          <tbody>
            {audit.models.map((m) => (
              <tr key={m.value}>
                <td>{m.label}<div className="cd-sub2">{m.provider} · {m.billing === 'flat-fee' ? 'flat fee' : m.billing === 'per-token' ? 'per token' : 'no cost'}</div></td>
                <td><BasisChip basis={m.basis} margin={m.basis === 'margin' ? m.markupPercent : null} /></td>
                <td className="r m">{m.billing === 'flat-fee' ? 'flat fee' : `${rate(m.costInput)} · ${rate(m.costOutput)}`}</td>
                <td className="r m">{rate(m.sellInput)} · {rate(m.sellOutput)}</td>
                <td>
                  {m.warnings.length === 0 && <span className="cd-chip g">OK</span>}
                  {m.warnings.map((w) => <div key={w} style={{ fontSize: 11.5, color: /loses|\$0|not charged/.test(w) ? 'var(--cd-red)' : 'var(--cd-muted)' }}>{w}</div>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {audit.unpricedInUsage.length > 0 && (
        <div style={{ padding: '10px 15px 14px', fontSize: 12 }}>
          <b>Used without a price in the last 30 days</b> <span className="cd-sub2">(those calls were charged $0)</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {audit.unpricedInUsage.map((u) => <span key={u.model} className="cd-chip r">{u.model} · {u.calls} calls · last {when(u.lastAt)}</span>)}
          </div>
        </div>
      )}
    </div>
  );
};

type Show = 'all' | 'charged' | 'not';

const CompanyUsageTab: React.FC = () => {
  const [period, setPeriod] = useState<Period>('month');
  const [rows, setRows] = useState<CompanyRow[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [hosting, setHosting] = useState<HostingTotals | null>(null);
  const [failed, setFailed] = useState('');
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [onlyUsed, setOnlyUsed] = useState(true);
  const navigate = useNavigate();
  const { companyId } = useParams();

  const load = useCallback(async () => {
    setFailed('');
    try {
      const { data } = await axios.get(API(), { params: { period } });
      setRows(data.companies);
      setAvailable(data.usage_available);
      setHosting(data.hosting || null);
    } catch (err) {
      setFailed(errorText(err, 'Could not load companies.'));
    }
  }, [period]);

  useEffect(() => {
    setRows(null);
    load();
  }, [load]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (onlyUsed && !r.calls && !r.box_seconds && !r.aws_charge_usd) return false;
      if (show === 'charged' && !r.charged) return false;
      if (show === 'not' && r.charged) return false;
      if (!q) return true;
      return `${r.name || ''} ${r.owner_email || ''}`.toLowerCase().includes(q);
    }).sort((a, b) => b.cost_usd - a.cost_usd);
  }, [rows, query, show, onlyUsed]);

  const totals = useMemo(() => shown.reduce((t, r) => ({
    ai: t.ai + r.ai_cost_usd,
    compute: t.compute + r.compute_cost_usd,
    aws: t.aws + r.aws_cost_usd,
    cost: t.cost + r.cost_usd,
    charged: t.charged + r.charged_usd,
    margin: t.margin + r.margin_usd,
    absorbed: t.absorbed + (r.charged ? 0 : r.cost_usd),
  }), { ai: 0, compute: 0, aws: 0, cost: 0, charged: 0, margin: 0, absorbed: 0 }), [shown]);

  const notCharged = (rows || []).filter((r) => r.not_billed_source === 'super_admin').length;
  const open = rows?.find((r) => r.id === companyId) || null;
  const periodTabs = (
    <div className="cd-tabs" role="tablist" aria-label="Period">
      {PERIODS.map(([id, label]) => (
        <button key={id} type="button" role="tab" aria-selected={period === id} className="cd-tab" data-on={period === id} onClick={() => setPeriod(id)}>{label}</button>
      ))}
    </div>
  );

  if (companyId) {
    return (
      <>
        <style>{MODAL_CSS}</style>
        {periodTabs}
        {failed && <div className="cd-note"><b>Couldn’t load this company.</b> {failed}</div>}
        {!rows && !failed && <div style={{ padding: 30, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} /></div>}
        {rows && !open && <div className="cd-note"><b>Company not found.</b> <button type="button" className="cd-link" onClick={() => navigate('/super/machine/usage')}>Back to Usage &amp; charging</button></div>}
        {open && <CompanyPage key={open.id} row={open} onBack={() => navigate('/super/machine/usage')} onChanged={load} />}
      </>
    );
  }

  return (
    <>
      <style>{MODAL_CSS}</style>
      {periodTabs}

      {failed && <div className="cd-note"><b>Couldn’t load companies.</b> {failed}</div>}
      {!available && <div className="cd-note"><b>Builder unreachable.</b> Charging status shows, usage figures appear once the builder answers.</div>}

      <div className="cd-kpis cu-k5">
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">AI cost</span></div><div className="v">{rows ? usd(totals.ai) : '—'}</div><div className="s">what the AI providers cost us</div></div>
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">E2B cost</span></div><div className="v">{rows ? usd(totals.compute) : '—'}</div><div className="s">workspace boxes</div></div>
        <div className="cd-kpi">
          <div className="lab"><span className="cd-eyebrow">AWS hosting</span><button type="button" className="cd-link" onClick={() => navigate('/super/machine/hosting')}>Bills</button></div>
          <div className="v">{hosting ? usd(hosting.bill_usd) : '—'}</div>
          <div className="s">{hosting && hosting.bills ? `${usd(hosting.charged_usd)} charged · ${usd(hosting.absorbed_usd)} absorbed` : 'no bill charged in this period'}</div>
        </div>
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">Charged</span></div><div className="v">{rows ? usd(totals.charged) : '—'}</div><div className="s">{rows ? `margin ${pct(totals.margin, totals.cost) || '—'}` : ''}</div></div>
        <div className="cd-kpi"><div className="lab"><span className="cd-eyebrow">Profit</span></div><div className="v" style={{ color: totals.margin < 0 ? 'var(--cd-red)' : 'var(--cd-good)' }}>{rows ? usd(totals.margin) : '—'}</div><div className="s">{rows && notCharged ? `${notCharged} not charged · ${usd(totals.absorbed)} absorbed` : 'charged minus cost'}</div></div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <input className="cd-input" style={{ maxWidth: 280 }} placeholder="Search company or owner" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search companies" />
        <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Show">
          {([['all', 'All'], ['charged', 'Charged'], ['not', 'Not charged']] as [Show, string][]).map(([id, label]) => (
            <button key={id} type="button" className={`cd-btn sm${show === id ? ' pri' : ''}`} aria-pressed={show === id} onClick={() => setShow(id)}>{label}</button>
          ))}
        </div>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
          <input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} /> Only companies with usage
        </label>
      </div>

      <div className="cd-panel">
        <div className="cd-ph"><span className="t">Usage by company</span><span className="cd-sub2">{rows ? `${shown.length} shown` : ''}</span></div>
        {!rows && !failed && <div style={{ padding: 30, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} /></div>}
        {rows && shown.length === 0 && <div className="cd-sub2" style={{ padding: 15 }}>No companies match.</div>}
        {rows && shown.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="cd-t">
              <thead><tr>
                <th>Company</th><th>Charging</th>
                <th className="r">AI cost</th><th className="r">AI margin</th><th className="r">E2B cost</th><th className="r">E2B margin</th>
                <th className="r" title="This company's share of the AWS bills charged for months in this period">AWS share</th>
                <th className="r">Charged</th><th className="r">Profit</th>
              </tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="cl" onClick={() => navigate(`/super/machine/usage/${r.id}`)}>
                    <td>{r.name || r.owner_email}<div className="cd-sub2">{PLAN_LABEL[r.plan]} · {r.calls} {r.calls === 1 ? 'call' : 'calls'}</div></td>
                    <td><ChargingChip row={r} /></td>
                    <td className="r m">{usd(r.ai_cost_usd)}</td>
                    <td className="r m" style={{ color: r.ai_charge_usd < r.ai_cost_usd ? 'var(--cd-red)' : undefined }}>{pct(r.ai_charge_usd - r.ai_cost_usd, r.ai_cost_usd) || 'n/a'}</td>
                    <td className="r m">{usd(r.compute_cost_usd)}</td>
                    <td className="r m" style={{ color: r.compute_charge_usd < r.compute_cost_usd ? 'var(--cd-red)' : undefined }}>{pct(r.compute_charge_usd - r.compute_cost_usd, r.compute_cost_usd) || 'n/a'}</td>
                    <td className="r m"><AwsShare row={r} /></td>
                    <td className="r m">{usd(r.charged_usd)}{!r.charged && r.would_charge_usd > 0 && <div className="cd-sub2">would be {usd(r.would_charge_usd)}</div>}</td>
                    <td className="r m" style={{ fontWeight: 600, color: r.margin_usd < 0 ? 'var(--cd-red)' : 'var(--cd-good)' }}>{usd(r.margin_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModelPricingPanel />

    </>
  );
};

export default CompanyUsageTab;
